import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { FeaturesPage } from "@/components/FeaturesPage";
import { PracticeTest } from "@/components/PracticeTest";
import { SubjectSelectionPage } from "@/components/SubjectSelectionPage";
import { SubjectDetailPage } from "@/components/SubjectDetailPage";
import { LoginPage } from "@/components/LoginPage";
import { SignUpPage } from "@/components/SignUpPage";
import { DashboardPage } from "@/components/DashboardPage";
import { PrivacyPage } from "@/components/PrivacyPage";
import { TermsPage } from "@/components/TermsPage";
import { SettingsPage } from "@/components/SettingsPage";
import { PDFUploadPortal } from "@/components/PDFUploadPortal";
import { AdminQuestionsPage } from "@/components/AdminQuestionsPage";
import { AdminLandingPage } from "@/components/AdminLandingPage";
import { AdminRefinePage } from "@/components/AdminRefinePage";
import { ForgotPasswordPage } from "@/components/ForgotPasswordPage";
import { ResetPasswordPage } from "@/components/ResetPasswordPage";
import { ReviewPage } from "@/components/ReviewPage";
import { PaywallPage } from "@/components/PaywallPage";
import { PaymentSuccessPage } from "@/components/PaymentSuccessPage";
import { AuthProvider } from "@/contexts/AuthContext";
import { SubjectsProvider } from "@/contexts/SubjectsContext";
import { client } from "@/lib/appwrite";
import { PreviewDashboardPage } from "@/components/PreviewDashboardPage";
import { PreviewPracticePage } from "@/components/PreviewPracticePage";
import MarketingLayout from "@/marketing/MarketingLayout";
import MarketingHomePage from "@/marketing/MarketingHomePage";
import SATPage from "@/marketing/SATPage";
import ACTPage from "@/marketing/ACTPage";
import APPage from "@/marketing/APPage";
import ELAPage from "@/marketing/ELAPage";
import CollegePage from "@/marketing/CollegePage";
import SubjectTutoringPage from "@/marketing/SubjectTutoringPage";
import AboutPage from "@/marketing/AboutPage";
import TutorsPage from "@/marketing/TutorsPage";
import ReviewsPage from "@/marketing/ReviewsPage";
import ContactPage from "@/marketing/ContactPage";
import "./index.css";

const ADMIN_EMAIL = "scholarsphere100@gmail.com";

function AdminGuard({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated, loading } = useAuth();
  useEffect(() => {
    if (!loading && (!isAuthenticated || user?.email !== ADMIN_EMAIL)) {
      window.location.replace("/login");
    }
  }, [loading, isAuthenticated, user]);
  if (loading || !isAuthenticated || user?.email !== ADMIN_EMAIL) return null;
  return <>{children}</>;
}

function AppRoutes() {
  const [path, setPath] = useState<string>("/");
  const [hash, setHash] = useState<string>("");

  useEffect(() => {
    if (typeof window !== "undefined") {
      setPath(window.location.pathname);
      setHash(window.location.hash);

      const handleHashChange = () => {
        setHash(window.location.hash);
      };

      const handlePopState = () => {
        setPath(window.location.pathname);
        setHash(window.location.hash);
      };

      window.addEventListener("hashchange", handleHashChange);
      window.addEventListener("popstate", handlePopState);
      return () => {
        window.removeEventListener("hashchange", handleHashChange);
        window.removeEventListener("popstate", handlePopState);
      };
    }
  }, []);

  // Preview routes — no auth, used as iframes on marketing site
  if (path === "/preview/dashboard") {
    return <PreviewDashboardPage />;
  }
  if (path === "/preview/practice") {
    return <PreviewPracticePage />;
  }

  // Auth routes
  if (path === "/login") {
    return <LoginPage />;
  }

  if (path === "/signup") {
    return <SignUpPage />;
  }

  if (path === "/forgot-password") {
    return <ForgotPasswordPage />;
  }

  if (path === "/reset-password") {
    return <ResetPasswordPage />;
  }

  // Dashboard
  if (path === "/dashboard") {
    return <DashboardPage />;
  }

  // Settings
  if (path === "/settings") {
    return <SettingsPage />;
  }

  // Admin landing page
  if (path === "/admin") {
    return <AdminGuard><AdminLandingPage /></AdminGuard>;
  }

  if (path === "/admin/upload") {
    return <AdminGuard><PDFUploadPortal /></AdminGuard>;
  }

  if (path === "/admin/review-questions") {
    return <AdminGuard><AdminQuestionsPage /></AdminGuard>;
  }

  if (path === "/admin/refine-explanations") {
    return <AdminGuard><AdminRefinePage /></AdminGuard>;
  }

  // Legal pages
  if (path === "/privacy") {
    return <PrivacyPage />;
  }

  if (path === "/terms") {
    return <TermsPage />;
  }

  // Subject routes
  if (path.startsWith("/subjects")) {
    if (path === "/subjects" || path === "/subjects/") {
      return <SubjectSelectionPage />;
    }
    return <SubjectDetailPage />;
  }

  // Practice test
  if (path.startsWith("/practice")) {
    return <PracticeTest />;
  }

  // Review mode
  if (path.startsWith("/review")) {
    return <ReviewPage />;
  }

  // Paywall
  if (path === "/paywall") {
    return <PaywallPage />;
  }

  // Payment success
  if (path === "/payment/success") {
    return <PaymentSuccessPage />;
  }

  // Features page
  if (path.startsWith("/features")) {
    return <FeaturesPage showOnlyReviews={hash === "#reviews"} />;
  }

  // Marketing pages — wrapped in the marketing site layout
  if (path === "/sat-prep") {
    return <MarketingLayout><SATPage /></MarketingLayout>;
  }
  if (path === "/act-prep" || path === "/act-tutoring") {
    return <MarketingLayout><ACTPage /></MarketingLayout>;
  }
  if (path === "/ap-physics" || path === "/ap-tutoring") {
    return <MarketingLayout><APPage /></MarketingLayout>;
  }
  if (path === "/english-tutoring") {
    return <MarketingLayout><ELAPage /></MarketingLayout>;
  }
  if (path === "/college-counseling") {
    return <MarketingLayout><CollegePage /></MarketingLayout>;
  }
  if (path === "/subject-tutoring") {
    return <MarketingLayout><SubjectTutoringPage /></MarketingLayout>;
  }
  if (path === "/about") {
    return <MarketingLayout><AboutPage /></MarketingLayout>;
  }
  if (path === "/tutors") {
    return <MarketingLayout><TutorsPage /></MarketingLayout>;
  }
  if (path === "/reviews") {
    return <MarketingLayout><ReviewsPage /></MarketingLayout>;
  }
  if (path === "/contact") {
    return <MarketingLayout><ContactPage /></MarketingLayout>;
  }

  // Home — full offerings landing page
  return (
    <MarketingLayout>
      <MarketingHomePage />
    </MarketingLayout>
  );
}

export function App() {

  return (
    <AuthProvider>
      <SubjectsProvider>
        <AppRoutes />
      </SubjectsProvider>
    </AuthProvider>
  );
}

export default App;
